import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { auditApi } from "api/audit";
import { teamApi } from "api/team";
import { getCurrentUser } from "api/client";

const ENTITY_TYPES = [
  "organization", "user", "lead", "contact", "conversation",
  "message", "task", "call", "deal", "company", "import", "alien",
];

const ACTIONS = [
  "create", "update", "delete", "send", "approve", "reject", "undo",
  "stage_move", "status_change", "opt_out", "ai_usage",
];

const ACTION_COLORS = {
  create: "#15803D",
  update: "#1D4ED8",
  delete: "#B91C1C",
  send: "#7C3AED",
  approve: "#047857",
  reject: "#B45309",
  undo: "#6B7280",
  stage_move: "#0E7490",
  status_change: "#0E7490",
  opt_out: "#B91C1C",
  ai_usage: "#A16207",
};

const PAGE_SIZE = 25;

export default function Activity() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [actorNames, setActorNames] = useState({});
  const [busyId, setBusyId] = useState("");

  const currentUser = getCurrentUser();
  const canUndo = currentUser?.role === "Owner" || currentUser?.role === "Manager";

  const load = useCallback(async (nextOffset = offset) => {
    setIsLoading(true);
    setError("");
    try {
      const result = await auditApi.list({
        entityType: entityType || undefined,
        action: action || undefined,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setEntries(result.items);
      setTotal(result.total);
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.message || "Unable to load activity.");
    } finally {
      setIsLoading(false);
    }
  }, [entityType, action, offset]);

  useEffect(() => {
    load(0);
  }, [entityType, action]);

  useEffect(() => {
    (async () => {
      try {
        const members = await teamApi.listMembers();
        setActorNames(Object.fromEntries(members.map((m) => [m.id, m.name])));
      } catch {
        // actor names are a nice-to-have; fall back to raw ids
      }
    })();
  }, []);

  const handleUndo = async (entry) => {
    if (!entry.undoToken) return;
    if (!window.confirm("Undo this change? (create → soft-delete, update → restore snapshot)")) return;
    setBusyId(entry.id);
    try {
      await auditApi.undo(entry.id);
      await load(offset);
    } catch (err) {
      setError(err?.message || "Unable to undo.");
    } finally {
      setBusyId("");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  const actorLabel = (entry) => {
    if (entry.actorType === "user") return actorNames[entry.actorId] || "User";
    return entry.actorType; // system | import | webhook | agent:<name>
  };

  const pageLabel = (entry) => {
    const t = entry.entityType;
    const a = entry.action;
    const byAction = {
      create: "created",
      update: "updated",
      delete: "deleted",
      send: "sent",
      approve: "approved",
      reject: "rejected",
      undo: "undid",
      stage_move: "moved",
      status_change: "changed status of",
      opt_out: "opted out",
      ai_usage: "used AI on",
    };
    return `${byAction[a] || a} ${t}`;
  };

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
            Activity
          </h1>
          <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            Every write in this workspace — human or agent — lands here, newest first
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="text-sm px-2 py-1.5 rounded-lg border font-caption"
            style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
            aria-label="Entity type"
          >
            <option value="">All entities</option>
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="text-sm px-2 py-1.5 rounded-lg border font-caption"
            style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
            aria-label="Action"
          >
            <option value="">All actions</option>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            {total} entr{total === 1 ? "y" : "ies"} · page {page}/{totalPages}
          </span>
        </div>

        {/* List */}
        <div className="rounded-xl border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          {isLoading ? (
            <p className="font-caption text-sm p-4" style={{ color: "var(--color-text-secondary)" }}>Loading…</p>
          ) : entries.length === 0 ? (
            <p className="font-caption text-sm p-4" style={{ color: "var(--color-text-secondary)" }}>
              No activity matches these filters.
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--color-border)" }}>
              {entries.map((entry) => (
                <li key={entry.id} className="px-4 py-3 flex items-start gap-3">
                  <span
                    className="mt-1 inline-block w-2 h-2 rounded-full shrink-0"
                    style={{ background: ACTION_COLORS[entry.action] || "#6B7280" }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-caption text-sm" style={{ color: "var(--color-foreground)" }}>
                      <span className="font-medium">{actorLabel(entry)}</span>{" "}
                      <span style={{ color: "var(--color-text-secondary)" }}>{pageLabel(entry)}</span>{" "}
                      {entry.sourceRef && (
                        <code className="text-xs px-1.5 py-0.5 rounded" style={{ background: "var(--color-muted)", color: "var(--color-text-secondary)" }}>
                          {entry.sourceRef}
                        </code>
                      )}
                    </p>
                    <p className="font-caption text-xs mt-0.5 break-all" style={{ color: "var(--color-text-secondary)" }}>
                      {entry.entityId}
                      <span className="ml-2">{entry.createdAtUtc ? new Date(entry.createdAtUtc).toLocaleString("en-IN") : ""}</span>
                    </p>
                  </div>
                  {canUndo && entry.undoToken && (
                    <Button variant="outline" size="sm" disabled={busyId === entry.id} onClick={() => handleUndo(entry)}>
                      <Icon name="Undo2" size={13} style={{ marginRight: 5 }} />
                      Undo
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => load(offset - PAGE_SIZE)}>
            ← Newer
          </Button>
          <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => load(offset + PAGE_SIZE)}>
            Older →
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
